import { routeConstants } from "./constants";
import {
  isCA,
  isEnLanguage,
  isUK,
  isUS,
  isDeLanguage,
  isNZ,
  isAU,
} from "./userSegment";
import { appConfig } from "src/config";

export enum HOLIDAY_PROMO_CODE {
  BLACKFRIDAY6 = "BLACKFRIDAY6",
  BLACKFRIDAY25 = "BLACKFRIDAY25",
  NEWYEAR6 = "NEWYEAR6",
  NEWYEAR25 = "NEWYEAR25",
  SUMMER6 = "SUMMER6",
  NEWYEAR10 = "NEWYEAR10",
  SPRING2025 = "SPRING2025",
  "1440-SPRING2025" = "1440-SPRING2025",
  MORNINGBREW2025 = "MORNINGBREW2025",
  SUMMER2025 = "SUMMER2025",
  "2U0N2H5O3O0O2N0M2E7" = "2U0N2H5O3O0O2N0M2E7",
  WINTER2025 = "WINTER2025",
  BFCM2025 = "BFCM2025",
  NYE2026 = "NYE2026",
}

export enum HOLIDAY_PROMO_TYPE {
  BLACK_FRIDAY_6_MONTHS_OFF = "BLACK_FRIDAY_6_MONTHS_OFF",
  BLACK_FRIDAY_25_PERCENT_OFF = "BLACK_FRIDAY_25_PERCENT_OFF",
  NEW_YEAR_6_MONTHS_OFF = "NEW_YEAR_6_MONTHS_OFF",
  NEW_YEAR_6_MONTHS_OFF_INTL_DIRECT = "NEW_YEAR_6_MONTHS_OFF_INTL_DIRECT",
  NEW_YEAR_25_PERCENT_OFF = "NEW_YEAR_25_PERCENT_OFF",
  SUMMER_SALE_6_MONTHS_OFF = "SUMMER_SALE_6_MONTHS_OFF",
  NEW_YEAR_10_PERCENT_OFF = "NEW_YEAR_10_PERCENT_OFF",
  SPRING_6_MONTHS_OFF = "SPRING_6_MONTHS_OFF",
  SPRING_20_PERCENT_OFF = "SPRING_20_PERCENT_OFF",
  SUMMER_6_MONTHS_OFF_2025 = "SUMMER_6_MONTHS_OFF_2025",
  COMPOUNDED_10_USD_OFF = "COMPOUNDED_10_USD_OFF",
  WINTER_6_MONTHS_OFF_2025 = "WINTER_6_MONTHS_OFF_2025",
  BFCM_6_MONTHS_OFF_2025 = "BFCM_6_MONTHS_OFF_2025",
  NYE_6_MONTHS_OFF_2026 = "NYE_6_MONTHS_OFF_2026",
  BFCM_UTM_CONTENT_6_MONTHS_OFF_2025 = "BFCM_UTM_CONTENT_6_MONTHS_OFF_2025",
  NEW_YEAR_UTM_CONTENT_6_MONTHS_OFF_2026 = "NEW_YEAR_UTM_CONTENT_6_MONTHS_OFF_2026",
}

export enum PROMO_BANNER_ACTIVATION_TYPE {
  URL_PARAM = "URL_PARAM",
  DATE_RANGE = "DATE_RANGE",
}

type BaseHolidayPromoConfig = {
  eligibleRoutes: routeConstants[];
  urlPromoValue?: string;
  customUrlParams?: Record<string, string>;
  uiPromoCode: HOLIDAY_PROMO_CODE;
  isUserSegmentEligible: () => boolean;
  translationKey: string;
  promoType: PROMO_TYPE;
  productTypeTranslationKey: PROMO_PRODUCT_TYPE;
  autoApplyPromoCode: boolean;
  activeFrom?: Date;
  activeTo?: Date;
  enabled: boolean;
  campaign?: string;
};

export type MonthsOffPromoConfig = BaseHolidayPromoConfig & {
  promoType: PROMO_TYPE.MONTHS_OFF;
  forcedPlanDuration: number;
  numberOfMonthsOff: number;
};

export type PercentOffPromoConfig = BaseHolidayPromoConfig & {
  promoType: PROMO_TYPE.PERCENTAGE_OFF;
  percentageOff: number;
};

export type FlatAmountOffPromoConfig = BaseHolidayPromoConfig & {
  promoType: PROMO_TYPE.FLAT_AMOUNT_OFF;
  flatAmountOff: number;
};

export type HolidayPromoConfig =
  | MonthsOffPromoConfig
  | PercentOffPromoConfig
  | FlatAmountOffPromoConfig;

const BASE_PROMO_ROUTES = [
  routeConstants.exsf01,
  routeConstants.exsf01d,
  routeConstants.direct,
];

export enum PROMO_TYPE {
  MONTHS_OFF = "MONTHS_OFF",
  PERCENTAGE_OFF = "PERCENTAGE_OFF",
  FLAT_AMOUNT_OFF = "FLAT_AMOUNT_OFF",
}

export enum PROMO_PRODUCT_TYPE {
  NOOM_WEIGHT = "noomWeight",
  NOOM_MED = "noomMed",
}

export const HOLIDAY_PROMO_CONFIG: Record<
  HOLIDAY_PROMO_TYPE,
  HolidayPromoConfig
> = {
  [HOLIDAY_PROMO_TYPE.BLACK_FRIDAY_6_MONTHS_OFF]: {
    eligibleRoutes: [...BASE_PROMO_ROUTES, routeConstants.affiliateTrialNPB],
    urlPromoValue: "6MonthBFCM2024",
    uiPromoCode: HOLIDAY_PROMO_CODE.BLACKFRIDAY6,
    forcedPlanDuration: 12,
    numberOfMonthsOff: 6,
    isUserSegmentEligible: () => true,
    translationKey: "blackFridayBannerMonthsOff",
    promoType: PROMO_TYPE.MONTHS_OFF,
    productTypeTranslationKey: PROMO_PRODUCT_TYPE.NOOM_WEIGHT,
    autoApplyPromoCode: true,
    enabled: false,
  },
  [HOLIDAY_PROMO_TYPE.BLACK_FRIDAY_25_PERCENT_OFF]: {
    eligibleRoutes: [...BASE_PROMO_ROUTES, routeConstants.affiliateTrialNPB],
    urlPromoValue: "25BFCM2024",
    uiPromoCode: HOLIDAY_PROMO_CODE.BLACKFRIDAY25,
    isUserSegmentEligible: () => isEnLanguage(),
    translationKey: "blackFridayBannerPercentageOff",
    promoType: PROMO_TYPE.PERCENTAGE_OFF,
    productTypeTranslationKey: PROMO_PRODUCT_TYPE.NOOM_WEIGHT,
    autoApplyPromoCode: true,
    enabled: false,
    percentageOff: 25,
  },
  [HOLIDAY_PROMO_TYPE.NEW_YEAR_6_MONTHS_OFF]: {
    eligibleRoutes: [...BASE_PROMO_ROUTES, routeConstants.affiliateTrialNPB],
    urlPromoValue: "6MonthNYE2024",
    uiPromoCode: HOLIDAY_PROMO_CODE.NEWYEAR6,
    forcedPlanDuration: 12,
    numberOfMonthsOff: 6,
    isUserSegmentEligible: () => true,
    translationKey: "newYearBannerMonthsOff",
    promoType: PROMO_TYPE.MONTHS_OFF,
    productTypeTranslationKey: PROMO_PRODUCT_TYPE.NOOM_WEIGHT,
    autoApplyPromoCode: true,
    enabled: false,
  },
  [HOLIDAY_PROMO_TYPE.NEW_YEAR_25_PERCENT_OFF]: {
    eligibleRoutes: [...BASE_PROMO_ROUTES, routeConstants.affiliateTrialNPB],
    urlPromoValue: "25NYE2024",
    uiPromoCode: HOLIDAY_PROMO_CODE.NEWYEAR25,
    isUserSegmentEligible: () => isEnLanguage(),
    translationKey: "newYearBannerPercentageOff",
    promoType: PROMO_TYPE.PERCENTAGE_OFF,
    productTypeTranslationKey: PROMO_PRODUCT_TYPE.NOOM_WEIGHT,
    autoApplyPromoCode: true,
    enabled: false,
    percentageOff: 25,
  },
  [HOLIDAY_PROMO_TYPE.SUMMER_SALE_6_MONTHS_OFF]: {
    eligibleRoutes: BASE_PROMO_ROUTES,
    urlPromoValue: "6MonthSummer2024",
    uiPromoCode: HOLIDAY_PROMO_CODE.SUMMER6,
    forcedPlanDuration: 12,
    numberOfMonthsOff: 6,
    isUserSegmentEligible: () => isEnLanguage() && !isUS(),
    translationKey: "summerBanner",
    promoType: PROMO_TYPE.MONTHS_OFF,
    productTypeTranslationKey: PROMO_PRODUCT_TYPE.NOOM_WEIGHT,
    autoApplyPromoCode: true,
    enabled: false,
  },
  [HOLIDAY_PROMO_TYPE.NEW_YEAR_10_PERCENT_OFF]: {
    eligibleRoutes: [routeConstants.clinical],
    urlPromoValue: "10NYE2024",
    uiPromoCode: HOLIDAY_PROMO_CODE.NEWYEAR10,
    isUserSegmentEligible: () => isEnLanguage() && isUS(),
    translationKey: "newYearMedBannerPercentageOff",
    promoType: PROMO_TYPE.PERCENTAGE_OFF,
    productTypeTranslationKey: PROMO_PRODUCT_TYPE.NOOM_MED,
    autoApplyPromoCode: true,
    enabled: false,
    percentageOff: 10,
  },
  [HOLIDAY_PROMO_TYPE.NEW_YEAR_6_MONTHS_OFF_INTL_DIRECT]: {
    eligibleRoutes: [routeConstants.direct],
    uiPromoCode: HOLIDAY_PROMO_CODE.NEWYEAR6,
    forcedPlanDuration: 12,
    numberOfMonthsOff: 6,
    isUserSegmentEligible: () => isEnLanguage && (isCA() || isUK()),
    translationKey: "newYearBannerMonthsOff",
    promoType: PROMO_TYPE.MONTHS_OFF,
    productTypeTranslationKey: PROMO_PRODUCT_TYPE.NOOM_WEIGHT,
    autoApplyPromoCode: false,
    activeFrom: new Date(appConfig.INTL_NYE_HOLIDAY_PROMO_BANNER_START_TIME),
    activeTo: new Date(appConfig.INTL_NYE_HOLIDAY_PROMO_BANNER_END_TIME),
    enabled: false,
  },
  [HOLIDAY_PROMO_TYPE.SPRING_6_MONTHS_OFF]: {
    eligibleRoutes: [routeConstants.direct, routeConstants.exsf01],
    urlPromoValue: "6MonthSpring2025",
    uiPromoCode: HOLIDAY_PROMO_CODE.SPRING2025,
    forcedPlanDuration: 12,
    numberOfMonthsOff: 6,
    isUserSegmentEligible: () => isEnLanguage() || isDeLanguage(),
    translationKey: "springBannerMonthsOff",
    promoType: PROMO_TYPE.MONTHS_OFF,
    productTypeTranslationKey: PROMO_PRODUCT_TYPE.NOOM_WEIGHT,
    autoApplyPromoCode: true,
    enabled: false,
  },
  [HOLIDAY_PROMO_TYPE.SPRING_20_PERCENT_OFF]: {
    eligibleRoutes: [routeConstants.affiliateTrialNPB],
    urlPromoValue: "MORNINGBREW2025",
    uiPromoCode: HOLIDAY_PROMO_CODE.MORNINGBREW2025,
    isUserSegmentEligible: () => isEnLanguage() && isUS(),
    translationKey: "MorningBrew2025BannerPercentageOff",
    promoType: PROMO_TYPE.PERCENTAGE_OFF,
    productTypeTranslationKey: PROMO_PRODUCT_TYPE.NOOM_WEIGHT,
    autoApplyPromoCode: true,
    enabled: true,
    percentageOff: 20,
  },
  [HOLIDAY_PROMO_TYPE.SUMMER_6_MONTHS_OFF_2025]: {
    eligibleRoutes: [routeConstants.direct, routeConstants.exsf01],
    urlPromoValue: "6MonthSummer2025",
    uiPromoCode: HOLIDAY_PROMO_CODE.SUMMER2025,
    forcedPlanDuration: 12,
    numberOfMonthsOff: 6,
    isUserSegmentEligible: () => isEnLanguage() && !isNZ() && !isAU(),
    translationKey: "summerBannerMonthsOff2025",
    promoType: PROMO_TYPE.MONTHS_OFF,
    productTypeTranslationKey: PROMO_PRODUCT_TYPE.NOOM_WEIGHT,
    autoApplyPromoCode: true,
    enabled: true,
  },
  [HOLIDAY_PROMO_TYPE.COMPOUNDED_10_USD_OFF]: {
    eligibleRoutes: [routeConstants.clinical],
    urlPromoValue: "NoomUHOne30",
    uiPromoCode: HOLIDAY_PROMO_CODE["2U0N2H5O3O0O2N0M2E7"],
    isUserSegmentEligible: () => isEnLanguage() && isUS(),
    translationKey: "compounded10Off",
    promoType: PROMO_TYPE.FLAT_AMOUNT_OFF,
    productTypeTranslationKey: PROMO_PRODUCT_TYPE.NOOM_MED,
    autoApplyPromoCode: true,
    enabled: true,
    flatAmountOff: 30,
    campaign: "UHOne",
  },
  [HOLIDAY_PROMO_TYPE.WINTER_6_MONTHS_OFF_2025]: {
    eligibleRoutes: [routeConstants.direct, routeConstants.exsf01],
    urlPromoValue: "6MonthWinter2025",
    uiPromoCode: HOLIDAY_PROMO_CODE.WINTER2025,
    forcedPlanDuration: 12,
    numberOfMonthsOff: 6,
    isUserSegmentEligible: () => isEnLanguage() && (isNZ() || isAU()),
    translationKey: "winterBannerMonthsOff2025",
    promoType: PROMO_TYPE.MONTHS_OFF,
    productTypeTranslationKey: PROMO_PRODUCT_TYPE.NOOM_WEIGHT,
    autoApplyPromoCode: true,
    enabled: true,
  },
  [HOLIDAY_PROMO_TYPE.BFCM_6_MONTHS_OFF_2025]: {
    eligibleRoutes: BASE_PROMO_ROUTES,
    urlPromoValue: "6MonthBFCM2025",
    uiPromoCode: HOLIDAY_PROMO_CODE.BFCM2025,
    forcedPlanDuration: 12,
    numberOfMonthsOff: 6,
    isUserSegmentEligible: () => isEnLanguage() || isDeLanguage(),
    translationKey: "bfcmBannerMonthsOff2025",
    promoType: PROMO_TYPE.MONTHS_OFF,
    productTypeTranslationKey: PROMO_PRODUCT_TYPE.NOOM_WEIGHT,
    activeFrom: new Date("2025-11-08T00:00:00.000"),
    activeTo: new Date("2025-12-03T23:59:59.000"),
    autoApplyPromoCode: true,
    enabled: true,
  },
  [HOLIDAY_PROMO_TYPE.NYE_6_MONTHS_OFF_2026]: {
    eligibleRoutes: BASE_PROMO_ROUTES,
    urlPromoValue: "6MonthNYE2026",
    uiPromoCode: HOLIDAY_PROMO_CODE.NYE2026,
    forcedPlanDuration: 12,
    numberOfMonthsOff: 6,
    isUserSegmentEligible: () => isEnLanguage() || isDeLanguage(),
    translationKey: "nyeBannerMonthsOff2026",
    promoType: PROMO_TYPE.MONTHS_OFF,
    productTypeTranslationKey: PROMO_PRODUCT_TYPE.NOOM_WEIGHT,
    autoApplyPromoCode: true,
    activeFrom: new Date("2025-12-20T00:00:00.000"),
    activeTo: new Date("2026-01-15T23:59:59.000"),
    enabled: true,
  },
  [HOLIDAY_PROMO_TYPE.BFCM_UTM_CONTENT_6_MONTHS_OFF_2025]: {
    eligibleRoutes: [routeConstants.exsf01],
    customUrlParams: { utm_content: "3282161" },
    uiPromoCode: HOLIDAY_PROMO_CODE.BFCM2025,
    forcedPlanDuration: 12,
    numberOfMonthsOff: 6,
    isUserSegmentEligible: () => isEnLanguage() || isDeLanguage(),
    translationKey: "bfcmBannerMonthsOff2025",
    promoType: PROMO_TYPE.MONTHS_OFF,
    productTypeTranslationKey: PROMO_PRODUCT_TYPE.NOOM_WEIGHT,
    activeFrom: new Date("2025-11-08T00:00:00.000"),
    activeTo: new Date("2025-12-03T23:59:59.000"),
    autoApplyPromoCode: true,
    enabled: true,
  },
  [HOLIDAY_PROMO_TYPE.NEW_YEAR_UTM_CONTENT_6_MONTHS_OFF_2026]: {
    eligibleRoutes: [routeConstants.exsf01],
    customUrlParams: { utm_content: "3282161" },
    uiPromoCode: HOLIDAY_PROMO_CODE.NYE2026,
    forcedPlanDuration: 12,
    numberOfMonthsOff: 6,
    isUserSegmentEligible: () => isEnLanguage() || isDeLanguage(),
    translationKey: "nyeBannerMonthsOff2026",
    promoType: PROMO_TYPE.MONTHS_OFF,
    productTypeTranslationKey: PROMO_PRODUCT_TYPE.NOOM_WEIGHT,
    activeFrom: new Date("2025-12-20T00:00:00.000"),
    activeTo: new Date("2026-01-15T23:59:59.000"),
    autoApplyPromoCode: true,
    enabled: true,
  },
};

export const MONTHS_OFF_PROMO_REPLACEMENT_CODES = {
  USD: {
    6: "MONTHSOFFPROMOUSD6",
  },
  CAD: {
    6: "MONTHSOFFPROMOCAD6",
  },
  NZD: {
    6: "MONTHSOFFPROMONZD6",
  },
  AUD: {
    6: "MONTHSOFFPROMOAUD6",
  },
  GBP: {
    6: "MONTHSOFFPROMOGBP6",
  },
  EUR: {
    6: "MONTHSOFFPROMOEUR6",
  },
} as const;

// Hardcoded post-discount prices to avoid having
// to use an api request to load the forced plan before checkout
export const priceMapping = {
  USD: {
    6: 159,
    12: 179,
  },
  CAD: {
    6: 129,
    12: 199,
  },
  NZD: {
    6: 169,
    12: 209,
  },
  AUD: {
    6: 149,
    12: 199,
  },
  GBP: {
    6: 59,
    12: 114,
  },
  EUR: {
    6: 99,
    12: 119,
  },
};

export const discountedPlanMapping = {
  USD: {
    12: 156.75,
  },
  CAD: {
    12: 201.75,
  },
  NZD: {
    12: 201.75,
  },
  AUD: {
    12: 201.75,
  },
  GBP: {
    12: 119.25,
  },
  EUR: {
    12: 123,
  },
};
